import { useEffect } from "react";
import { useMap } from "react-leaflet";
import type { GeoJSON as CapaLeaflet, Layer, LayersControlEvent } from "leaflet";
import type { GenericLayer } from "../model/genericLayer.ts";

/**
 * Reflects the map's own `LayersControl` overlay checkboxes back into
 * `GenericLayer.visible` — the `checked` prop (SubBasinMap.tsx) only drives
 * the INITIAL state of each overlay (native Leaflet control, not a React
 * component: toggling its checkbox doesn't run through React at all), so
 * without this, clicking a generic layer's checkbox in the layers menu
 * would change what's drawn on the map but leave `genericLayers` (and
 * GenericLayersPanel.tsx's own list) unaware of it. Matches which layer
 * was toggled by comparing the raw Leaflet layer Leaflet's own event
 * carries (`e.layer`) against `layerRefs`, keyed by `GenericLayer.id` —
 * not by name (the event's `name` is whatever ReactNode was rendered,
 * fragile to match back to an id if two layers ever share a name).
 */
export function GenericLayersSync({
  genericLayers,
  onChangeGenericLayers,
  layerRefs,
}: {
  genericLayers: GenericLayer[];
  onChangeGenericLayers: (layers: GenericLayer[]) => void;
  layerRefs: React.MutableRefObject<Map<string, CapaLeaflet>>;
}) {
  const map = useMap();
  useEffect(() => {
    function idOf(target: Layer): string | undefined {
      for (const [id, l] of layerRefs.current) if (l === target) return id;
      return undefined;
    }
    function setVisible(e: LayersControlEvent, visible: boolean) {
      const id = idOf(e.layer);
      if (!id) return;
      onChangeGenericLayers(genericLayers.map((l) => (l.id === id ? { ...l, visible } : l)));
    }
    const onAdd = (e: LayersControlEvent) => setVisible(e, true);
    const onRemove = (e: LayersControlEvent) => setVisible(e, false);
    map.on("overlayadd", onAdd);
    map.on("overlayremove", onRemove);
    return () => {
      map.off("overlayadd", onAdd);
      map.off("overlayremove", onRemove);
    };
  }, [map, genericLayers, onChangeGenericLayers, layerRefs]);
  return null;
}
